import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, DollarSign, Clock, Package2,
  Truck, CheckCircle, AlertTriangle, Printer, Mail,
  Box, ClipboardCheck, Tag, UserCheck, FileText,
  PauseCircle, XCircle, RefreshCcw, Factory, AlertCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { useInventory } from '../context/InventoryContext';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Badge from '../components/ui/Badge';
import { ManufacturedProduct, SalesOrderItem } from '../types';

interface OrderItem {
  product_id?: string;
  manufactured_product_id?: string;
  is_manufactured: boolean;
  quantity: number;
  unit_price: number;
  serial_number?: string;
}

interface PaymentRecord {
  id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  reference_number?: string;
  notes?: string;
}

interface ProcessingStep {
  id: string;
  name: string;
  status: 'pending' | 'completed' | 'failed';
  notes?: string;
  completed_at?: string;
}

interface WeOweItem {
  id: string;
  product_id?: string;
  manufactured_product_id?: string;
  is_manufactured: boolean;
  quantity: number;
  reason: string;
  promised_date?: string;
  status: 'pending' | 'fulfilled' | 'cancelled';
  notes?: string;
  created_at: string;
  fulfilled_at?: string;
}

const SalesOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { products } = useInventory();
  const isNewOrder = id === 'new';
  const [manufacturedProducts, setManufacturedProducts] = useState<ManufacturedProduct[]>([]);
  const [bomValidationErrors, setBomValidationErrors] = useState<Record<number, string>>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<OrderItem[]>([]);
  const [orderStatus, setOrderStatus] = useState('new');
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>([]);
  const [weOweItems, setWeOweItems] = useState<WeOweItem[]>([]);
  const [showWeOweModal, setShowWeOweModal] = useState(false);
  const [newPayment, setNewPayment] = useState({
    amount: 0,
    payment_method: 'cash',
    reference_number: '',
    notes: ''
  });
  const [newWeOwe, setNewWeOwe] = useState({
    product_id: '',
    manufactured_product_id: '',
    is_manufactured: false,
    quantity: 1,
    reason: '',
    promised_date: '',
    notes: ''
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!isNewOrder) {
          await Promise.all([
            loadManufacturedProducts(),
            loadSalesOrder(),
            loadOrderItems(),
            loadPayments(),
            loadProcessingSteps(),
            loadWeOweItems()
          ]);
        } else {
          await loadManufacturedProducts();
          generateOrderNumber();
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load order data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const loadManufacturedProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('manufactured_products')
        .select('*')
        .eq('user_id', user?.id)
        .eq('status', 'production');

      if (error) throw error;
      setManufacturedProducts(data || []);
    } catch (error) {
      console.error('Error loading manufactured products:', error);
    }
  };

  const loadSalesOrder = async () => {
    try {
      const { data: order, error } = await supabase
        .from('sales_orders')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (order) {
        setOrderNumber(order.order_number);
        setOrderDate(order.order_date ? new Date(order.order_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
        setDeliveryDate(order.delivery_date ? new Date(order.delivery_date).toISOString().split('T')[0] : '');
        setCustomerName(order.customer_name);
        setCustomerEmail(order.customer_email || '');
        setCustomerPhone(order.customer_phone || '');
        setNotes(order.notes || '');
        setOrderStatus(order.status);
        setPaymentStatus(order.payment_status);
      }
    } catch (error) {
      console.error('Error loading sales order:', error);
      setError('Failed to load sales order');
    }
  };

  const loadOrderItems = async () => {
    try {
      const { data, error } = await supabase
        .from('sales_order_items')
        .select(`
          id,
          product_id,
          manufactured_product_id,
          is_manufactured,
          quantity,
          unit_price,
          serial_number,
          status
        `)
        .eq('sales_order_id', id);

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error loading order items:', error);
      setError('Failed to load order items');
    }
  };

  const loadPayments = async () => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('sales_order_id', id)
        .order('payment_date', { ascending: false });

      if (error) throw error;
      setPayments(data || []);
    } catch (error) {
      console.error('Error loading payments:', error);
    }
  };

  const loadProcessingSteps = async () => {
    try {
      const { data, error } = await supabase
        .from('order_processing')
        .select('*')
        .eq('sales_order_id', id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setProcessingSteps(data || []);
    } catch (error) {
      console.error('Error loading processing steps:', error);
    }
  };

  const loadWeOweItems = async () => {
    try {
      const { data, error } = await supabase
        .from('we_owe_items')
        .select('*')
        .eq('sales_order_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWeOweItems(data || []);
    } catch (error) {
      console.error('Error loading we owe items:', error);
    }
  };

  const generateOrderNumber = () => {
    const prefix = 'SO';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setOrderNumber(`${prefix}-${timestamp}-${random}`);
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setLoading(true);

      // If changing to 'shipped' status, validate BOM availability for all manufactured items
      if (newStatus === 'shipped') {
        const manufacturedItems = items.filter(item => item.is_manufactured && item.manufactured_product_id);

        for (const item of manufacturedItems) {
          const mfgProduct = manufacturedProducts.find(p => p.id === item.manufactured_product_id);
          if (!mfgProduct) continue;

          const { data, error } = await supabase
            .rpc('validate_bom_availability', {
              p_user_id: user?.id,
              p_bom: mfgProduct.bill_of_materials,
              p_quantity: item.quantity
            });

          if (error) throw error;

          if (data && data.length > 0 && !data[0].is_available) {
            setError(`Cannot ship order: Insufficient components for "${mfgProduct.name}". ${JSON.stringify(data[0].missing_components)}`);
            setLoading(false);
            return;
          }
        }
      }

      // Update order status
      const { error } = await supabase
        .from('sales_orders')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Update all order items status to trigger BOM deduction
      if (newStatus === 'shipped') {
        const { error: itemsError } = await supabase
          .from('sales_order_items')
          .update({ status: 'shipped' })
          .eq('sales_order_id', id)
          .eq('status', 'pending');

        if (itemsError) throw itemsError;

        // Reload items to reflect status change
        await loadOrderItems();
      }

      setOrderStatus(newStatus);

      // Record status change in audit trail
      await supabase.from('status_changes').insert({
        sales_order_id: id,
        old_status: orderStatus,
        new_status: newStatus,
        changed_by: user?.email,
        notes: `Status changed from ${orderStatus} to ${newStatus}`
      });

    } catch (error) {
      console.error('Error updating status:', error);
      setError(error instanceof Error ? error.message : 'Failed to update order status');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const { error: paymentError } = await supabase
        .from('payments')
        .insert({
          sales_order_id: id,
          amount: newPayment.amount,
          payment_method: newPayment.payment_method,
          reference_number: newPayment.reference_number,
          notes: newPayment.notes,
          created_by: user?.id
        });

      if (paymentError) throw paymentError;

      // Update order payment status
      const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0) + newPayment.amount;
      const totalAmount = calculateTotal();
      
      let newPaymentStatus = 'pending';
      if (totalPaid >= totalAmount) {
        newPaymentStatus = 'paid';
      } else if (totalPaid > 0) {
        newPaymentStatus = 'partial';
      }

      const { error: orderError } = await supabase
        .from('sales_orders')
        .update({ payment_status: newPaymentStatus })
        .eq('id', id);

      if (orderError) throw orderError;

      setPaymentStatus(newPaymentStatus);
      setShowPaymentModal(false);
      setNewPayment({
        amount: 0,
        payment_method: 'cash',
        reference_number: '',
        notes: ''
      });
      loadPayments();

    } catch (error) {
      console.error('Error processing payment:', error);
      setError('Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const handleWeOweSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Determine which product to use
      const productData: any = {
        sales_order_id: id,
        quantity: newWeOwe.quantity,
        reason: newWeOwe.reason,
        promised_date: newWeOwe.promised_date || null,
        notes: newWeOwe.notes || null,
        created_by: user?.id,
        status: 'pending'
      };

      if (newWeOwe.is_manufactured) {
        productData.manufactured_product_id = newWeOwe.manufactured_product_id;
        productData.product_id = null;
      } else {
        productData.product_id = newWeOwe.product_id;
        productData.manufactured_product_id = null;
      }
      productData.is_manufactured = newWeOwe.is_manufactured;

      const { error: weOweError } = await supabase
        .from('we_owe_items')
        .insert(productData);

      if (weOweError) throw weOweError;

      setShowWeOweModal(false);
      setNewWeOwe({
        product_id: '',
        manufactured_product_id: '',
        is_manufactured: false,
        quantity: 1,
        reason: '',
        promised_date: '',
        notes: ''
      });
      loadWeOweItems();

    } catch (error) {
      console.error('Error creating we owe item:', error);
      setError('Failed to create we owe item');
    } finally {
      setLoading(false);
    }
  };

  const handleWeOweFulfilled = async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('we_owe_items')
        .update({
          status: 'fulfilled',
          fulfilled_at: new Date().toISOString()
        })
        .eq('id', itemId);

      if (error) throw error;
      loadWeOweItems();
    } catch (error) {
      console.error('Error marking we owe item as fulfilled:', error);
      setError('Failed to update we owe item');
    }
  };

  const handleWeOweDelete = async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('we_owe_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      loadWeOweItems();
    } catch (error) {
      console.error('Error deleting we owe item:', error);
      setError('Failed to delete we owe item');
    }
  };

  const handleAddItem = () => {
    setItems([...items, { product_id: '', is_manufactured: false, quantity: 1, unit_price: 0 }]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: keyof OrderItem, value: string | number | boolean) => {
    const newItems = [...items];
    if (field === 'product_id' && typeof value === 'string') {
      const product = products.find(p => p.id === value);
      newItems[index] = {
        ...newItems[index],
        product_id: value,
        manufactured_product_id: undefined,
        is_manufactured: false,
        unit_price: product?.price || 0
      };
    } else if (field === 'manufactured_product_id' && typeof value === 'string') {
      const mfgProduct = manufacturedProducts.find(p => p.id === value);
      newItems[index] = {
        ...newItems[index],
        product_id: undefined,
        manufactured_product_id: value,
        is_manufactured: true,
        unit_price: mfgProduct?.price || 0
      };
      validateBOMAvailability(index, value, newItems[index].quantity);
    } else if (field === 'quantity' && newItems[index].is_manufactured && newItems[index].manufactured_product_id) {
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
      validateBOMAvailability(index, newItems[index].manufactured_product_id!, value as number);
    } else {
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
    }
    setItems(newItems);
  };

  const validateBOMAvailability = async (index: number, productId: string, quantity: number) => {
    try {
      const { data, error } = await supabase
        .rpc('validate_bom_availability', {
          p_user_id: user?.id,
          p_bom: (manufacturedProducts.find(p => p.id === productId)?.bill_of_materials || []),
          p_quantity: quantity
        });

      if (error) throw error;

      const newErrors = { ...bomValidationErrors };
      if (data && data.length > 0) {
        const result = data[0];
        if (!result.is_available) {
          const missing = result.missing_components;
          newErrors[index] = `Insufficient components: ${JSON.stringify(missing).substring(0, 100)}...`;
        } else {
          delete newErrors[index];
        }
      }
      setBomValidationErrors(newErrors);
    } catch (error) {
      console.error('Error validating BOM:', error);
    }
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const orderData = {
        user_id: user?.id,
        order_number: orderNumber,
        order_date: orderDate,
        delivery_date: deliveryDate || null,
        customer_name: customerName,
        customer_email: customerEmail || null,
        customer_phone: customerPhone || null,
        notes: notes || null,
        total_amount: calculateTotal(),
        status: orderStatus,
        payment_status: paymentStatus,
        currency: 'USD'
      };

      if (isNewOrder) {
        const { data: order, error: orderError } = await supabase
          .from('sales_orders')
          .insert([orderData])
          .select()
          .single();

        if (orderError) throw orderError;

        const { error: itemsError } = await supabase
          .from('sales_order_items')
          .insert(
            items.map(item => ({
              sales_order_id: order.id,
              product_id: item.product_id || null,
              manufactured_product_id: item.manufactured_product_id || null,
              is_manufactured: item.is_manufactured,
              quantity: item.quantity,
              unit_price: item.unit_price,
              serial_number: item.serial_number || null
            }))
          );

        if (itemsError) throw itemsError;

        navigate('/sales');
      } else {
        const { error: orderError } = await supabase
          .from('sales_orders')
          .update(orderData)
          .eq('id', id);

        if (orderError) throw orderError;

        const { error: deleteError } = await supabase
          .from('sales_order_items')
          .delete()
          .eq('sales_order_id', id);

        if (deleteError) throw deleteError;

        const { error: itemsError } = await supabase
          .from('sales_order_items')
          .insert(
            items.map(item => ({
              sales_order_id: id,
              product_id: item.product_id || null,
              manufactured_product_id: item.manufactured_product_id || null,
              is_manufactured: item.is_manufactured,
              quantity: item.quantity,
              unit_price: item.unit_price,
              serial_number: item.serial_number || null
            }))
          );

        if (itemsError) throw itemsError;

        // If order is delivered and has a delivery date, update warranty dates directly
        if (orderStatus === 'delivered' && deliveryDate) {
          // Use RPC function to update warranty dates with proper date calculation
          const { error: warrantyUpdateError } = await supabase.rpc(
            'update_warranty_dates_for_order',
            {
              p_sales_order_id: id,
              p_new_delivery_date: deliveryDate
            }
          );

          if (warrantyUpdateError) {
            console.error('Error updating warranty dates:', warrantyUpdateError);
          }
        }

        // Reload the order to show updated values
        await loadSalesOrder();
        alert('Sales order updated successfully. Warranty dates have been updated for all products in this order.');
      }
    } catch (error) {
      console.error('Error saving sales order:', error);
      setError('Failed to save sales order');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!customerName.trim()) {
      setError('Customer name is required');
      return false;
    }
    if (items.length === 0) {
      setError('At least one item is required');
      return false;
    }
    if (items.some(item => !item.product_id && !item.manufactured_product_id)) {
      setError('Please select products or manufactured products for all items');
      return false;
    }
    if (Object.keys(bomValidationErrors).length > 0) {
      setError('Some manufactured products have insufficient components available');
      return false;
    }
    if (items.some(item => item.quantity <= 0)) {
      setError('Quantity must be greater than 0');
      return false;
    }
    return true;
  };

  const statusOptions = [
    { value: 'new', label: 'New', icon: Clock },
    { value: 'processing', label: 'Processing', icon: RefreshCcw },
    { value: 'shipped', label: 'Shipped', icon: Truck },
    { value: 'delivered', label: 'Delivered', icon: CheckCircle },
    { value: 'cancelled', label: 'Cancelled', icon: XCircle }
  ];

  const paymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'check', label: 'Check' },
    { value: 'other', label: 'Other' }
  ];

  const productOptions = products.map(product => ({
    value: product.id,
    label: `${product.name} (${product.sku}) - $${product.price}`
  }));

  const manufacturedProductOptions = manufacturedProducts.map(product => ({
    value: product.id,
    label: `${product.name} (${product.sku}) - $${product.price} [Manufactured]`
  }));

  const allProductOptions = [
    { value: '', label: '-- Select Product --', disabled: true },
    ...productOptions,
    ...manufacturedProductOptions
  ];

  const getProductName = (item: OrderItem) => {
    if (item.is_manufactured && item.manufactured_product_id) {
      const mfgProduct = manufacturedProducts.find(p => p.id === item.manufactured_product_id);
      return mfgProduct?.name || 'Unknown Manufactured Product';
    } else if (item.product_id) {
      const product = products.find(p => p.id === item.product_id);
      return product?.name || 'Unknown Product';
    }
    return 'No Product Selected';
  };

  const getWeOweProductName = (item: WeOweItem) => {
    if (item.is_manufactured && item.manufactured_product_id) {
      const mfgProduct = manufacturedProducts.find(p => p.id === item.manufactured_product_id);
      return mfgProduct?.name || 'Unknown Manufactured Product';
    } else if (item.product_id) {
      const product = products.find(p => p.id === item.product_id);
      return product?.name || 'Unknown Product';
    }
    return 'Unknown Product';
  };

  if (loading && !orderNumber) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/sales-orders')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNewOrder ? 'Create Sales Order' : `Edit Sales Order: ${orderNumber}`}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Order Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Order Number"
                  value={orderNumber}
                  disabled
                  fullWidth
                />
                <Select
                  label="Status"
                  options={statusOptions}
                  value={orderStatus}
                  onChange={handleStatusChange}
                  fullWidth
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Order Date"
                  type="date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  fullWidth
                />
                <Input
                  label="Date Delivered (for warranty)"
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  fullWidth
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Customer Name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  fullWidth
                />
                <Input
                  label="Customer Email"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  fullWidth
                />
              </div>

              <Input
                label="Customer Phone"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                fullWidth
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Order Items</CardTitle>
                <Button onClick={handleAddItem}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {items.map((item, index) => {
                  const productName = getProductName(item);
                  const productSku = item.is_manufactured
                    ? manufacturedProducts.find(p => p.id === item.manufactured_product_id)?.sku
                    : products.find(p => p.id === item.product_id)?.sku;

                  return (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg">
                    {/* Product Header */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{productName}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            {productSku && (
                              <span className="text-sm text-gray-500">SKU: {productSku}</span>
                            )}
                            {item.is_manufactured && (
                              <Badge variant="primary">
                                <Factory className="h-3 w-3 mr-1" />
                                Manufactured Product
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      {bomValidationErrors[index] && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                          <AlertTriangle className="h-3 w-3 inline mr-1" />
                          {bomValidationErrors[index]}
                        </div>
                      )}
                    </div>

                    {/* Product Selection Dropdown */}
                    <div className="mb-4">
                      <Select
                        label="Change Product"
                        options={allProductOptions}
                        value={item.is_manufactured ? item.manufactured_product_id : item.product_id}
                        onChange={(value) => {
                          const isMfg = manufacturedProductOptions.some(opt => opt.value === value);
                          if (isMfg) {
                            handleItemChange(index, 'manufactured_product_id', value);
                          } else {
                            handleItemChange(index, 'product_id', value);
                          }
                        }}
                        fullWidth
                        required
                      />
                    </div>

                    {/* Quantity and Price */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <Input
                        label="Quantity"
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                        required
                        fullWidth
                      />
                      <Input
                        label="Unit Price"
                        type="number"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        required
                        fullWidth
                      />
                    </div>

                    {/* Serial Number */}
                    <Input
                      label="Serial Number (Optional - for warranty tracking)"
                      type="text"
                      value={item.serial_number || ''}
                      onChange={(e) => handleItemChange(index, 'serial_number', e.target.value)}
                      placeholder="Enter unique serial number"
                      fullWidth
                    />
                  </div>
                )})}


                {items.length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    No items added yet. Click "Add Item" to start adding products.
                  </div>
                )}

                {items.length > 0 && (
                  <div className="flex justify-end pt-4 border-t">
                    <div className="text-right">
                      <span className="text-sm text-gray-500">Total:</span>
                      <span className="ml-2 text-lg font-bold">${calculateTotal().toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Payment Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Status:</span>
                <Badge variant={paymentStatus === 'paid' ? 'success' : 'warning'}>
                  {paymentStatus}
                </Badge>
              </div>

              {!isNewOrder && (
                <Button
                  onClick={() => setShowPaymentModal(true)}
                  fullWidth
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Add Payment
                </Button>
              )}

              {payments.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Payment History</h4>
                  {payments.map(payment => (
                    <div
                      key={payment.id}
                      className="bg-gray-50 p-3 rounded-lg text-sm"
                    >
                      <div className="flex justify-between mb-1">
                        <span className="font-medium">${payment.amount.toFixed(2)}</span>
                        <span className="text-gray-500">{new Date(payment.payment_date).toLocaleDateString()}</span>
                      </div>
                      <div className="text-gray-600">{payment.payment_method}</div>
                      {payment.reference_number && (
                        <div className="text-gray-500 text-xs">
                          Ref: {payment.reference_number}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {!isNewOrder && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    <CardTitle>We Owe</CardTitle>
                  </div>
                  <Badge variant={weOweItems.filter(item => item.status === 'pending').length > 0 ? 'danger' : 'success'}>
                    {weOweItems.filter(item => item.status === 'pending').length} Pending
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Track items that were not delivered or delayed
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={() => setShowWeOweModal(true)}
                  fullWidth
                  variant="secondary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add We Owe Item
                </Button>

                {weOweItems.length > 0 ? (
                  <div className="space-y-2">
                    {weOweItems.map(item => (
                      <div
                        key={item.id}
                        className={`p-3 rounded-lg border ${
                          item.status === 'pending'
                            ? 'bg-amber-50 border-amber-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {getWeOweProductName(item)}
                              </span>
                              <Badge variant={item.status === 'pending' ? 'warning' : 'success'} size="sm">
                                {item.status}
                              </Badge>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Qty: {item.quantity} • Reason: {item.reason}
                            </div>
                            {item.promised_date && (
                              <div className="text-xs text-gray-500 mt-1">
                                Promised: {new Date(item.promised_date).toLocaleDateString()}
                              </div>
                            )}
                            {item.notes && (
                              <div className="text-xs text-gray-500 mt-1">
                                {item.notes}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1">
                            {item.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleWeOweFulfilled(item.id)}
                                className="text-green-600 hover:text-green-900"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleWeOweDelete(item.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No items owed. All items delivered successfully!
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {!isNewOrder && processingSteps.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Processing Steps</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {processingSteps.map(step => (
                    <div
                      key={step.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm font-medium">{step.name}</span>
                      <Badge
                        variant={
                          step.status === 'completed' ? 'success' :
                          step.status === 'failed' ? 'danger' :
                          'warning'
                        }
                      >
                        {step.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button
          variant="secondary"
          onClick={() => navigate('/sales-orders')}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : (isNewOrder ? 'Create Order' : 'Update Order')}
        </Button>
      </div>

      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Add Payment</CardTitle>
            </CardHeader>
            <form onSubmit={handlePaymentSubmit}>
              <CardContent className="space-y-4">
                <Input
                  label="Amount"
                  type="number"
                  step="0.01"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: parseFloat(e.target.value) || 0 })}
                  required
                  fullWidth
                />
                
                <Select
                  label="Payment Method"
                  options={paymentMethodOptions}
                  value={newPayment.payment_method}
                  onChange={(value) => setNewPayment({ ...newPayment, payment_method: value })}
                  required
                  fullWidth
                />

                <Input
                  label="Reference Number"
                  value={newPayment.reference_number}
                  onChange={(e) => setNewPayment({ ...newPayment, reference_number: e.target.value })}
                  fullWidth
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setNewPayment({
                      amount: 0,
                      payment_method: 'cash',
                      reference_number: '',
                      notes: ''
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                >
                  Add Payment
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      )}

      {showWeOweModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Add We Owe Item</CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                Track items that were not delivered or delayed
              </p>
            </CardHeader>
            <form onSubmit={handleWeOweSubmit}>
              <CardContent className="space-y-4">
                <Select
                  label="Product"
                  options={allProductOptions}
                  value={newWeOwe.is_manufactured ? newWeOwe.manufactured_product_id : newWeOwe.product_id}
                  onChange={(value) => {
                    const isMfg = manufacturedProductOptions.some(opt => opt.value === value);
                    setNewWeOwe({
                      ...newWeOwe,
                      product_id: isMfg ? '' : value,
                      manufactured_product_id: isMfg ? value : '',
                      is_manufactured: isMfg
                    });
                  }}
                  required
                  fullWidth
                />

                <Input
                  label="Quantity"
                  type="number"
                  min="1"
                  value={newWeOwe.quantity}
                  onChange={(e) => setNewWeOwe({ ...newWeOwe, quantity: parseInt(e.target.value) || 1 })}
                  required
                  fullWidth
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newWeOwe.reason}
                    onChange={(e) => setNewWeOwe({ ...newWeOwe, reason: e.target.value })}
                    className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2"
                    required
                  >
                    <option value="">Select reason...</option>
                    <option value="Out of stock">Out of stock</option>
                    <option value="Damaged in shipping">Damaged in shipping</option>
                    <option value="Wrong item sent">Wrong item sent</option>
                    <option value="Quality issue">Quality issue</option>
                    <option value="Delayed delivery">Delayed delivery</option>
                    <option value="Backordered">Backordered</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <Input
                  label="Promised Date"
                  type="date"
                  value={newWeOwe.promised_date}
                  onChange={(e) => setNewWeOwe({ ...newWeOwe, promised_date: e.target.value })}
                  fullWidth
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={newWeOwe.notes}
                    onChange={(e) => setNewWeOwe({ ...newWeOwe, notes: e.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Additional details about the issue..."
                  />
                </div>
              </CardContent>
              <CardFooter className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowWeOweModal(false);
                    setNewWeOwe({
                      product_id: '',
                      manufactured_product_id: '',
                      is_manufactured: false,
                      quantity: 1,
                      reason: '',
                      promised_date: '',
                      notes: ''
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                >
                  Add We Owe Item
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SalesOrderDetailPage;